
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Clock, Moon, Sunrise, BatteryMedium } from "lucide-react";

// Sample sleep data - In a real app, this would come from an API
const weeklyData = [
  { 
    day: "Mon", 
    hoursSlept: 7.2, 
    deepSleep: 2.1, 
    remSleep: 1.8, 
    lightSleep: 3.3,
    sleepScore: 82,
    bedTime: "10:45 PM",
    wakeTime: "6:00 AM" 
  },
  { 
    day: "Tue", 
    hoursSlept: 6.8, 
    deepSleep: 1.9, 
    remSleep: 1.5, 
    lightSleep: 3.4,
    sleepScore: 75,
    bedTime: "11:30 PM",
    wakeTime: "6:15 AM" 
  },
  { 
    day: "Wed", 
    hoursSlept: 8.1, 
    deepSleep: 2.4, 
    remSleep: 2.0, 
    lightSleep: 3.7,
    sleepScore: 88,
    bedTime: "10:15 PM",
    wakeTime: "6:20 AM" 
  },
  { 
    day: "Thu", 
    hoursSlept: 7.5, 
    deepSleep: 2.2, 
    remSleep: 1.8, 
    lightSleep: 3.5,
    sleepScore: 84,
    bedTime: "10:30 PM",
    wakeTime: "6:00 AM" 
  },
  { 
    day: "Fri", 
    hoursSlept: 7.0, 
    deepSleep: 2.0, 
    remSleep: 1.7, 
    lightSleep: 3.3,
    sleepScore: 79,
    bedTime: "11:00 PM",
    wakeTime: "6:00 AM" 
  },
  { 
    day: "Sat", 
    hoursSlept: 8.5, 
    deepSleep: 2.6, 
    remSleep: 2.2, 
    lightSleep: 3.7,
    sleepScore: 92,
    bedTime: "10:00 PM",
    wakeTime: "6:30 AM" 
  },
  { 
    day: "Sun", 
    hoursSlept: 8.0, 
    deepSleep: 2.3, 
    remSleep: 2.1, 
    lightSleep: 3.6,
    sleepScore: 88,
    bedTime: "10:15 PM",
    wakeTime: "6:15 AM" 
  },
];

const monthlyData = weeklyData.map(item => ({
  ...item,
  day: `Week ${Math.floor(Math.random() * 4) + 1}`
}));

const sleepStageColors = {
  deepSleep: "#5850EC",
  remSleep: "#6B7280",
  lightSleep: "#9CA3AF",
  hoursSlept: "#3B82F6"
};

export function SleepSection() {
  const [period, setPeriod] = useState("weekly");
  const [chartType, setChartType] = useState("hours");

  const data = period === "weekly" ? weeklyData : monthlyData;
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <Tabs value={period} onValueChange={setPeriod} className="w-[160px]">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="weekly">Weekly</TabsTrigger>
            <TabsTrigger value="monthly">Monthly</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={chartType} onValueChange={setChartType} className="w-[220px]">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="hours">Sleep Hours</TabsTrigger>
            <TabsTrigger value="stages">Sleep Stages</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="h-[300px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === "hours" ? (
            <LineChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis 
                dataKey="day" 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                domain={[0, dataMax => Math.max(dataMax, 10)]}
                label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(229, 231, 235, 1)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="hoursSlept"
                name="Total Sleep"
                stroke={sleepStageColors.hoursSlept}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          ) : (
            <BarChart
              data={data}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
              stackOffset="expand"
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
              <XAxis 
                dataKey="day" 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(229, 231, 235, 1)',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="deepSleep" 
                name="Deep Sleep" 
                stackId="a" 
                fill={sleepStageColors.deepSleep} 
              />
              <Bar 
                dataKey="remSleep" 
                name="REM Sleep" 
                stackId="a" 
                fill={sleepStageColors.remSleep} 
              />
              <Bar 
                dataKey="lightSleep" 
                name="Light Sleep" 
                stackId="a" 
                fill={sleepStageColors.lightSleep} 
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" /> Sleep Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">7.6 hrs</div>
            <p className="text-xs text-muted-foreground">Avg. sleep duration</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Moon className="h-4 w-4" /> Bedtime
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">10:30 PM</div>
            <p className="text-xs text-muted-foreground">Avg. bedtime</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sunrise className="h-4 w-4" /> Wake Up
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">6:10 AM</div>
            <p className="text-xs text-muted-foreground">Avg. wake time</p>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BatteryMedium className="h-5 w-5" /> Sleep Quality
          </CardTitle>
          <CardDescription>Your weekly sleep quality score</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
                <XAxis 
                  dataKey="day" 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  axisLine={{ stroke: 'rgba(107, 114, 128, 0.2)' }}
                  domain={[0, 100]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0.5rem',
                    border: '1px solid rgba(229, 231, 235, 1)',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                />
                <Bar 
                  dataKey="sleepScore" 
                  name="Sleep Score" 
                  fill="#5850EC" 
                  radius={[4, 4, 0, 0]} 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
